import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";

const UPDATED = "June 2026";

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] mb-2">{title}</h2>
      <div className="text-[#4A5361] text-sm leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-2xl bg-primary flex items-center justify-center text-white"><Sparkles size={18} /></span>
          <span className="font-heading font-extrabold text-xl tracking-tight">Errandell</span>
        </Link>
        <Link to="/" data-testid="terms-back-link" className="flex items-center gap-1 text-sm font-semibold text-[#6B7280] hover:text-[#1A1D20]">
          <ArrowLeft size={16} /> Back
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <div data-testid="terms-content" className="bg-white rounded-3xl border border-gray-100 shadow-soft p-7 sm:p-10">
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20] mb-1">Terms & Privacy Policy</h1>
          <p className="text-[#9CA3AF] text-sm mb-8">Last updated: {UPDATED}</p>

          <p className="text-[#4A5361] text-sm leading-relaxed mb-8">
            Welcome to Errandell ("Errandell", "we", "us"). These Terms of Service and Privacy Policy
            govern your use of the Errandell platform, which connects neighbors who post errands
            ("Requesters") with neighbors who complete them ("Runners"). By creating an account or
            using Errandell, you agree to these terms. This document is a general-purpose template and
            is not legal advice.
          </p>

          <h2 className="font-heading font-black text-2xl text-primary mb-4">Privacy Policy</h2>

          <Section title="1. Our privacy-first approach">
            <p>Errandell is designed to collect the <strong>minimum</strong> personal information needed to
            operate. We do <strong>not</strong> collect or store your real legal name. You are identified across the
            platform only by an anonymous display alias (for example, "Brave Otter 42").</p>
          </Section>

          <Section title="2. Information we collect">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Account data:</strong> your email address (for login and account recovery) and a securely hashed password. Passwords are never stored in plain text.</li>
              <li><strong>Display alias:</strong> a nickname you choose or that we generate for you.</li>
              <li><strong>Errand data:</strong> details you post (title, description, category, budget, deadline, and a general location for the task).</li>
              <li><strong>Live location (optional):</strong> if a Runner chooses to share their location for a task, it is used only to display progress during that active errand and is not retained as location history.</li>
              <li><strong>Ratings & reviews</strong> you leave or receive.</li>
            </ul>
          </Section>

          <Section title="3. Information we do NOT collect">
            <ul className="list-disc pl-5 space-y-1">
              <li>Real legal names.</li>
              <li>Full payment card numbers — all payments are processed by Stripe; we never see or store your card details.</li>
              <li>We do not sell, rent, or share your personal data with advertisers or data brokers.</li>
            </ul>
          </Section>

          <Section title="4. How we use your information">
            <p>We use your information solely to operate the service: to authenticate you, match errands
            with runners, process payments, calculate trust scores and ratings, and keep the community safe.
            We do not use your data for advertising or profiling.</p>
          </Section>

          <Section title="5. Payments">
            <p>Payments and the $7 cancellation fee are handled by Stripe, Inc. under their own terms and
            privacy policy. Errandell receives only a payment confirmation and the transaction amount — never
            your card details.</p>
          </Section>

          <Section title="6. Data retention & your rights">
            <p>We keep account and errand data only as long as your account is active or as needed to provide
            the service. You may request access to, correction of, or deletion of your account and associated
            data at any time by contacting us. Deleting your account removes your profile and personal data,
            subject to limited records we must retain for legal or financial (e.g., payment) purposes.</p>
          </Section>

          <Section title="7. Security">
            <p>We protect your data with industry-standard measures: passwords are hashed with bcrypt, sessions
            use signed tokens, public profiles never expose your email, and browsing endpoints require
            authentication to prevent bulk scraping or data mining. No system is perfectly secure, but we work
            to safeguard your information.</p>
          </Section>

          <Section title="8. Children">
            <p>Errandell is not intended for anyone under 16 (or the minimum age required in your jurisdiction).
            We do not knowingly collect data from children.</p>
          </Section>

          <h2 className="font-heading font-black text-2xl text-primary mb-4 mt-10">Terms of Service</h2>

          <Section title="9. Eligibility & accounts">
            <p>You must be able to form a binding contract to use Errandell. You are responsible for keeping your
            login credentials secure and for all activity under your account.</p>
          </Section>

          <Section title="10. Errands, payments & cancellations">
            <ul className="list-disc pl-5 space-y-1">
              <li>Requesters set a budget; Runners accept and complete errands.</li>
              <li>Payment to a Runner is released <strong>only after</strong> the Requester marks the errand complete. Funds are held until then.</li>
              <li>A <strong>$7 cancellation fee</strong> applies when a Requester cancels an errand after a Runner has accepted it. Runners may step down from an accepted errand without a fee, and the errand reopens for others.</li>
            </ul>
          </Section>

          <Section title="11. Code of Conduct & prohibited content">
            <p>Errandell is a community built on trust and respect. By posting any content — including errand
            titles, descriptions, messages, service listings, reviews, profile details, or any other copy — you
            agree to keep every request <strong>appropriate, lawful, and respectful</strong>. You must not post,
            request, or transmit any content that:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>contains <strong>sexual or sexually explicit references</strong>, solicitation, or adult content;</li>
              <li>contains <strong>violent references</strong>, threats of harm, or content that encourages or glorifies violence;</li>
              <li>contains <strong>racial, ethnic, religious, gender, or other slurs</strong>, or hateful content targeting any group;</li>
              <li>is <strong>harassing, bullying, demeaning, discriminatory, or threatening</strong> toward any person;</li>
              <li>is illegal, fraudulent, deceptive, or promotes illegal goods, services, or activity.</li>
            </ul>
            <p>To help enforce this, Errandell automatically screens submitted content and will
            <strong> block posts</strong> that appear to violate this Code of Conduct. Attempting to evade these
            filters is itself a violation.</p>
          </Section>

          <Section title="12. Enforcement, suspension & bans">
            <p>Errandell <strong>reserves the right, at its sole discretion and for any reason, to remove content,
            restrict features, suspend or permanently ban any user, and deny or withhold services</strong> — with or
            without prior notice. This includes, without limitation, violations of this Code of Conduct, unsafe or
            abusive behavior, misuse of the platform, or conduct we determine is harmful to the community or to
            Errandell. A ban or denial of service does not entitle you to any refund except as required by law.
            We may also cooperate with law enforcement where appropriate.</p>
          </Section>

          <Section title="13. Disclaimers & liability">
            <p>Errandell is a neutral platform that connects neighbors and does not employ Runners or guarantee
            outcomes. To the fullest extent permitted by law, Errandell is provided "as is" without warranties,
            and our liability is limited to the amount you paid to us in the preceding 12 months.</p>
          </Section>

          <Section title="14. Governing law">
            <p>These terms are governed by the laws of the State of California, USA, without regard to conflict
            of law principles.</p>
          </Section>

          <Section title="15. Contact">
            <p>Questions about these terms or your data? Contact us at <a href="mailto:privacy@errandell.com" className="text-primary font-semibold hover:underline">privacy@errandell.com</a>.</p>
          </Section>

          <div className="pt-4">
            <Link to="/register"><span data-testid="terms-signup-link" className="text-primary font-bold hover:underline text-sm">← Back to sign up</span></Link>
          </div>
        </div>
      </main>
    </div>
  );
}
